import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import { APP_BODY_FONT_SIZE_PX } from "@/shared/styles/typography";
import { MOBILE_BOTTOM_NAV_HEIGHT_PX } from "@/shared/ui/appHeader/mobileBottomNav";
import { CHROME_BOTTOM_VAR, CHROME_OFFSET_VAR } from "@/shared/ui/compactChrome/chromeCss";
import { FILTER_VALUE_TRIGGER_BORDER_RADIUS_PX } from "@/shared/ui/filters/filterTriggerStyles";
import { TABLE_FOOTER_CLEARANCE_VAR } from "@/shared/ui/tableFooterClearance";
import { HEADER_COMPACT_BREAKPOINT_PX } from "@/shared/ui/useCompactLayout";

export const SYSTEM_BANNER_FONT_SIZE_PX = APP_BODY_FONT_SIZE_PX;
export const SYSTEM_BANNER_ICON_SIZE_PX = 16;

/** Face and size of a system-banner message. Error and unavailable lines use the same. */
export const bannerMessageSx = {
  fontFamily: "var(--font-sans)",
  fontSize: `${SYSTEM_BANNER_FONT_SIZE_PX}px`,
  lineHeight: `${SYSTEM_BANNER_ICON_SIZE_PX}px`,
} as const;
export const SYSTEM_BANNER_HEIGHT_PX = 35;
export const SYSTEM_BANNER_ICON_TEXT_GAP_PX = 8;
/** Toast Alert icon and close glyph — slightly smaller than the full-bleed banner. */
export const TOAST_ALERT_ICON_SIZE_PX = 15;
export const TOAST_ALERT_CLOSE_PADDING_PX = 5;

const alertPartsSx = {
  "& .MuiAlert-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    margin: 0,
    padding: 0,
    height: SYSTEM_BANNER_ICON_SIZE_PX,
    minHeight: SYSTEM_BANNER_ICON_SIZE_PX,
    "& .MuiSvgIcon-root": {
      boxSizing: "border-box",
      display: "block",
      fontSize: SYSTEM_BANNER_ICON_SIZE_PX,
      width: SYSTEM_BANNER_ICON_SIZE_PX,
      height: SYSTEM_BANNER_ICON_SIZE_PX,
    },
  },
  "& .MuiAlert-message": {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
    fontFamily: "inherit",
    fontWeight: "inherit",
    fontSize: "inherit",
    lineHeight: "inherit",
    padding: 0,
    margin: 0,
  },
  "& .MuiAlert-action": {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
    padding: 0,
    margin: 0,
    marginLeft: "auto",
    // Plain CSS so the theme override matches what `sx` already rendered
    // (a bare 8 is theme.spacing(8), 64px).
    paddingLeft: "64px",
    "& .MuiIconButton-root": {
      padding: "4px",
    },
    "& .MuiSvgIcon-root": {
      fontSize: SYSTEM_BANNER_ICON_SIZE_PX,
      width: SYSTEM_BANNER_ICON_SIZE_PX,
      height: SYSTEM_BANNER_ICON_SIZE_PX,
    },
  },
} as const;

const bannerTypographySx = {
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  gap: `${SYSTEM_BANNER_ICON_TEXT_GAP_PX}px`,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: "16px",
  paddingRight: "16px",
  fontFamily: "var(--font-sans)",
  fontWeight: 700,
  fontSize: `${SYSTEM_BANNER_FONT_SIZE_PX}px`,
  lineHeight: `${SYSTEM_BANNER_ICON_SIZE_PX}px`,
  ...alertPartsSx,
} as const;

/**
 * Full-bleed extras on top of the theme Alert chrome: locked 35px height,
 * square corners, and a clipped message. Type, icon, gap, and Close live on
 * `MuiAlert` in the theme via {@link pageAlertSx}.
 */
export const systemBannerSx = {
  height: SYSTEM_BANNER_HEIGHT_PX,
  minHeight: SYSTEM_BANNER_HEIGHT_PX,
  maxHeight: SYSTEM_BANNER_HEIGHT_PX,
  borderRadius: 0,
  "& .MuiAlert-message": {
    overflow: "hidden",
  },
} as const satisfies SxProps<Theme>;

/**
 * In-page Alert chrome: Inter/sans · 9.75px · bold · 16px icon · 8px gap ·
 * 24px Close. Applied once as the `MuiAlert` theme override. No fixed height,
 * so longer messages can wrap; single-line alerts stay at least 35px tall.
 */
export const pageAlertSx = {
  ...bannerTypographySx,
  minHeight: SYSTEM_BANNER_HEIGHT_PX,
} as const satisfies SxProps<Theme>;

/** Text-style Retry action inside a banner Alert. Inherits the banner face and color. */
export const bannerRetryButtonSx = {
  minWidth: 0,
  minHeight: 0,
  padding: "0 9px",
  borderRadius: `${FILTER_VALUE_TRIGGER_BORDER_RADIUS_PX}px`,
  fontFamily: "inherit",
  fontWeight: "inherit",
  fontSize: `${SYSTEM_BANNER_FONT_SIZE_PX}px`,
  lineHeight: `${SYSTEM_BANNER_ICON_SIZE_PX}px`,
} as const;

/** One lap around the toast. Linear so the loop does not hesitate at the seam. */
const TOAST_BORDER_FLOW = "toastBorderFlow 1.1s linear infinite";

/**
 * Soft comet: the bright color eases in from the dim border and eases back out,
 * instead of peaking over a short arc.
 */
const TOAST_BORDER_GRADIENT = [
  "conic-gradient(from 0deg",
  "var(--toast-border-dim) 0deg",
  "color-mix(in srgb, var(--toast-border-bright) 8%, var(--toast-border-dim)) 32deg",
  "color-mix(in srgb, var(--toast-border-bright) 32%, var(--toast-border-dim)) 78deg",
  "color-mix(in srgb, var(--toast-border-bright) 68%, var(--toast-border-dim)) 118deg",
  "var(--toast-border-bright) 150deg",
  "color-mix(in srgb, var(--toast-border-bright) 68%, var(--toast-border-dim)) 182deg",
  "color-mix(in srgb, var(--toast-border-bright) 32%, var(--toast-border-dim)) 222deg",
  "color-mix(in srgb, var(--toast-border-bright) 8%, var(--toast-border-dim)) 268deg",
  "var(--toast-border-dim) 300deg",
  "var(--toast-border-dim) 360deg)",
].join(", ");

function toastBorderTone(color: string) {
  return {
    "--toast-border-dim": alpha(color, 0.2),
    "--toast-border-bright": color,
  };
}

/**
 * 1px ring. A bright segment orbits the rounded rectangle; the center keeps the
 * Alert fill. The spinner is a square rotating about the toast center, clipped
 * to the border radius, with an inset cover over the message.
 */
const toastFlowingBorderSxBase = {
  position: "relative",
  overflow: "hidden",
  "@keyframes toastBorderFlow": {
    to: { transform: "rotate(360deg)" },
  },
  "&::before": {
    content: '""',
    position: "absolute",
    zIndex: 0,
    left: "50%",
    top: "50%",
    width: "1200px",
    height: "1200px",
    marginLeft: "-600px",
    marginTop: "-600px",
    pointerEvents: "none",
    background: TOAST_BORDER_GRADIENT,
    animation: TOAST_BORDER_FLOW,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    zIndex: 0,
    inset: "1px",
    borderRadius: "inherit",
    pointerEvents: "none",
    backgroundColor: "inherit",
  },
  "& .MuiAlert-icon, & .MuiAlert-message, & .MuiAlert-action": {
    position: "relative",
    zIndex: 1,
  },
  "@media (prefers-reduced-motion: reduce)": {
    overflow: "visible",
    boxShadow: "inset 0 0 0 1px var(--toast-border-bright)",
    "&::before, &::after": {
      display: "none",
    },
  },
} as const;

/**
 * Runs the severity color along the toast border while it is open.
 * Composed onto {@link toastAlertSx} by AppToast only.
 */
export const toastFlowingBorderSx = (theme: Theme) => ({
  ...toastFlowingBorderSxBase,
  "&.MuiAlert-colorError": toastBorderTone(theme.palette.error.main),
  "&.MuiAlert-colorWarning": toastBorderTone(theme.palette.warning.main),
  "&.MuiAlert-colorInfo": toastBorderTone(theme.palette.info.main),
  "&.MuiAlert-colorSuccess": toastBorderTone(theme.palette.success.main),
});

/**
 * Toast-only deltas on top of the theme Alert chrome ({@link pageAlertSx}):
 * a 15px status icon, a 15px line box, and a 15px close glyph (5px padding).
 * Type, gap, centering, and the action offset stay on the theme.
 */
export const toastAlertSx = {
  lineHeight: `${TOAST_ALERT_ICON_SIZE_PX}px`,
  "& .MuiAlert-icon": {
    height: TOAST_ALERT_ICON_SIZE_PX,
    minHeight: TOAST_ALERT_ICON_SIZE_PX,
    "& .MuiSvgIcon-root": {
      fontSize: TOAST_ALERT_ICON_SIZE_PX,
      width: TOAST_ALERT_ICON_SIZE_PX,
      height: TOAST_ALERT_ICON_SIZE_PX,
    },
  },
  "& .MuiAlert-action": {
    "& .MuiIconButton-root": {
      padding: `${TOAST_ALERT_CLOSE_PADDING_PX}px`,
      borderRadius: `${FILTER_VALUE_TRIGGER_BORDER_RADIUS_PX}px`,
    },
    "& .MuiSvgIcon-root": {
      fontSize: TOAST_ALERT_ICON_SIZE_PX,
      width: TOAST_ALERT_ICON_SIZE_PX,
      height: TOAST_ALERT_ICON_SIZE_PX,
    },
  },
} as const satisfies SxProps<Theme>;

/**
 * Anchor passed to MUI. Below `md` the snackbar is centered with CSS; from `md`
 * up this anchor is the bottom-left corner.
 */
export const toastAnchorOrigin = { vertical: "bottom", horizontal: "left" } as const;

/**
 * Viewport inset for the toast stack. Below `md` it is the side gutter of a
 * centered toast. From `md` up it is the left-corner inset. The centered rules
 * are repeated at `sm` so MUI's own left inset does not win between 600px and 900px.
 */
export const TOAST_INSET_PX = 24;

/** Gap between stacked toasts. The offset already includes one gap per toast underneath. */
export const TOAST_STACK_GAP_PX = 8;

/** Pixels this toast sits above the corner inset. The newest toast is `0`. */
export const TOAST_STACK_OFFSET_VAR = "--toast-stack-offset";

/**
 * Bottom edge of a toast: the inset, then any toasts underneath, then whatever
 * mobile bottom chrome is still on screen.
 *
 * `--chrome-bottom` is only set by the compact chrome engine, which runs below
 * `md` (900px) and already includes the table pagination bar. Below
 * {@link HEADER_COMPACT_BREAKPOINT_PX} (990px) the fixed bottom nav is on
 * screen, so the 900-989px band has the nav with no chrome variable set.
 * `chromeFallbackPx` is the nav height to clear in that band; desktop
 * (>= 990px) passes `0` because there is no nav.
 *
 * From `md` up the pagination bar is not part of `--chrome-bottom`, so
 * `tableFooter` adds `--table-footer-clearance` and the toast sits one inset
 * above that bar. Below `md` the flag stays off so the footer is not counted
 * twice.
 */
function toastCornerBottom(chromeFallbackPx = 0, tableFooter = false): string {
  const chrome = `var(${CHROME_BOTTOM_VAR}, ${chromeFallbackPx}px)`;
  const aboveChrome = `max(0px, ${chrome} - var(${CHROME_OFFSET_VAR}, 0px))`;
  const footer = tableFooter ? ` + var(${TABLE_FOOTER_CLEARANCE_VAR}, 0px)` : "";
  return `calc(${TOAST_INSET_PX}px + env(safe-area-inset-bottom, 0px) + var(${TOAST_STACK_OFFSET_VAR}, 0px) + ${aboveChrome}${footer})`;
}

/**
 * Shared toast width. Short messages stay this wide. A longer message widens
 * the toast just enough to keep that text on one line, up to the viewport inset;
 * beyond that ceiling the message wraps rather than overflowing the toast.
 */
export const TOAST_WIDTH_PX = 280;
const toastFloor = `min(${TOAST_WIDTH_PX}px, calc(100vw - ${TOAST_INSET_PX * 2}px))`;
const toastCeiling = `calc(100vw - ${TOAST_INSET_PX * 2}px)`;

const toastSize = {
  width: "max-content",
  minWidth: toastFloor,
  maxWidth: toastCeiling,
  boxSizing: "border-box",
  "& .MuiAlert-root": {
    width: "max-content",
    minWidth: toastFloor,
    maxWidth: toastCeiling,
    boxSizing: "border-box",
  },
  // Short messages keep their intrinsic single-line width (the container is
  // `max-content`). Once the toast hits its ceiling the message must be allowed
  // to shrink and wrap — including unbroken watchlist names — so the tail and
  // the trailing Close button are not clipped by AppToast's `overflow: hidden`.
  "&& .MuiAlert-message": {
    flex: "0 1 auto",
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    overflow: "visible",
  },
} as const;

/** Fixed-width toast, horizontally centered. `left`/`right` 0 let `margin` auto center it. */
const toastCentered = {
  ...toastSize,
  top: "auto",
  left: 0,
  right: 0,
  bottom: toastCornerBottom(MOBILE_BOTTOM_NAV_HEIGHT_PX),
  marginLeft: "auto",
  marginRight: "auto",
  justifyContent: "center",
} as const;

/**
 * Bottom-left corner, one inset above the table pagination bar. Resets the
 * centered margins so they do not linger from `sm`.
 */
const toastCorner = {
  ...toastSize,
  top: "auto",
  right: "auto",
  left: `calc(${TOAST_INSET_PX}px + env(safe-area-inset-left, 0px))`,
  bottom: toastCornerBottom(MOBILE_BOTTOM_NAV_HEIGHT_PX, true),
  marginLeft: 0,
  marginRight: 0,
  justifyContent: "flex-start",
} as const;

export const toastSnackbarSx: SxProps<Theme> = (theme) => ({
  ...toastCentered,
  transition: theme.transitions.create("bottom", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
  }),
  [theme.breakpoints.up("sm")]: toastCentered,
  [theme.breakpoints.up("md")]: toastCorner,
  // From 990px up there is no bottom nav. Keep the toast one inset above the
  // table pagination bar while one is mounted.
  [theme.breakpoints.up(HEADER_COMPACT_BREAKPOINT_PX)]: {
    bottom: toastCornerBottom(0, true),
  },
});
